/**
 * iter63 — Legacy Pocketlite URL redirects.
 *
 * The old site lived at https://sridurgafoods.com using PHP-style paths
 * like `/home/contact`, `/home/product_details/23` etc.  After the
 * migration these URLs reach the React SPA, no route matches and the
 * shell renders header + footer with empty content.  Google has these
 * URLs cached and so do every external backlink, so we map each known
 * legacy path to its new equivalent and bounce the user with
 * `<Navigate replace>` — equivalent of a 301 redirect from a
 * crawler's perspective once the JS runs.
 *
 * IMPORTANT: every redirect is wrapped in `<MetaRobotsNoindex />` so the
 * old URL itself is dropped from the index (the new canonical URL is
 * the only one we want Google to keep).  We also set window.location
 * via `replace` so the back button doesn't bounce the user back to the
 * dead URL.
 */
import { useEffect } from "react";
import { useParams, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SITE_ORIGIN } from "../lib/seo";
import api from "../lib/api";

const MetaNoindex = ({ to }) => (
  <Helmet>
    <meta name="robots" content="noindex,follow" />
    {to && <link rel="canonical" href={`${SITE_ORIGIN}${to}`} />}
  </Helmet>
);

/** Generic static redirect — used for legacy paths with a 1:1 mapping. */
export const LegacyRedirect = ({ to }) => (
  <>
    <MetaNoindex to={to} />
    <Navigate to={to} replace />
  </>
);

/**
 * Old product URL: /home/product_details/:legacyId
 *
 * The legacy numeric IDs (23, 89, 144 etc.) don't exist in our Mongo
 * collection any more, so we can't resolve them to a specific product.
 * The pragmatic SEO move is to 302 the user to /shop with a banner
 * explaining the migration — preferable to a hard 404 because the link
 * still has equity, and customers landing from old backlinks reach the
 * catalogue rather than a dead end.
 */
export const LegacyProductRedirect = () => {
  const { id } = useParams();
  // Try resolving the legacy numeric id from `localStorage.legacyMap`
  // if the user has previously navigated through the new site (future
  // upgrade: we could persist the mapping server-side).  For now we
  // unconditionally redirect to /shop.
  void id;
  return (
    <>
      <MetaNoindex to="/shop" />
      <Navigate to="/shop" replace state={{ legacyProductId: id }} />
    </>
  );
};

/**
 * 404 page rendered for ANY unmatched route — replaces the old "blank
 * page" symptom.  Header + Footer stay (rendered by App.js) so the
 * customer still has navigation.  Noindexed because we don't want
 * Google to index our 404s.
 */
export const NotFound = () => {
  const loc = useLocation();
  useEffect(() => {
    // Tag Manager / GA4 — fire a virtual pageview so 404 traffic is
    // visible in analytics.
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: "page_not_found",
        page_path: loc.pathname + loc.search,
      });
    }
  }, [loc.pathname, loc.search]);
  return (
    <div className="container-x py-24 text-center" data-testid="not-found-page">
      <Helmet>
        <title>Page not found · Sri Durga Foods</title>
        <meta name="robots" content="noindex,follow" />
        <meta name="description" content="The page you're looking for has moved or no longer exists. Browse our collection of authentic Andhra pickles, spices and culinary pastes." />
      </Helmet>
      <div className="text-xs uppercase tracking-[0.3em] text-brand-chili mb-3">404</div>
      <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark mb-4">
        We couldn&apos;t find that page
      </h1>
      <p className="text-brand-muted max-w-xl mx-auto mb-10">
        The link you followed may be from our older site. Pick up where you
        left off — our full collection of Andhra pickles, pastes, spices
        and wood-pressed oils is one tap away.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <a href="/" className="btn-outline" data-testid="not-found-home-link">Go home</a>
        <a href="/shop" className="btn-primary" data-testid="not-found-shop-link">Shop the collection</a>
      </div>
      <div className="mt-12 text-xs uppercase tracking-[0.2em] text-brand-muted">
        Or browse by category
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-center text-sm">
        {[
          ["Pickles", "/pickles"],
          ["Culinary Pastes", "/pastes"],
          ["Wood Pressed Oils", "/wood-pressed-oils"],
          ["Podis", "/podis"],
          ["Masala Powders", "/masala-powders"],
          ["Spices", "/spices"],
          ["Flours", "/flours"],
        ].map(([label, path]) => (
          <a
            key={path}
            href={path}
            className="px-4 py-1.5 rounded-full border border-brand-line hover:border-brand-chili hover:text-brand-chili transition-colors"
            data-testid={`not-found-cat-${path.slice(1)}`}
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
};

/**
 * Backwards-compat: `/product/:productId` continues to resolve, but we
 * ask the backend for the new SEO slug and 301 to it client-side so
 * crawlers consolidate link equity on the canonical URL.
 */
import { useState } from "react";

export const ProductIdRedirect = () => {
  const { id } = useParams();
  const [slugPath, setSlugPath] = useState(null);
  useEffect(() => {
    let active = true;
    api.get(`/products/${id}/slug`).then((r) => {
      if (active) setSlugPath(r.data?.seo?.url_path || null);
    }).catch(() => active && setSlugPath("notfound"));
    return () => { active = false; };
  }, [id]);

  if (slugPath === null) {
    return (
      <div className="container-x py-24 text-center text-brand-muted">
        Loading…
      </div>
    );
  }
  if (slugPath === "notfound") return <NotFound />;
  return (
    <>
      <MetaNoindex to={slugPath} />
      <Navigate to={slugPath} replace />
    </>
  );
};
